/**
 * What makes one exam a different exam.
 *
 * The app started as a telc simulator, so telc's shape — four modules, 59
 * points, one paper graded into B1 or B2 — was simply the shape of the code.
 * The state exam (egzamin certyfikatowy) does not fit it: every level is its
 * own paper, the pass mark is a percentage of each part rather than an absolute
 * score, and the point totals differ from sitting to sitting.
 *
 * So the numbers move out of the module scope and into a *variant*: one exam,
 * at one level, for one candidate group. `structure.ts` defines telc's,
 * `certyfikat.ts` the state exam's, and `families.ts` collects them. Nothing
 * here knows about either — this file is only the shape they both fill in.
 *
 * The module ids are deliberately telc's. "Poprawność gramatyczna" and
 * "Elementy języka" test the same thing in the same way, so the state exam
 * reuses `elements`, and with it the whole attempt machinery: the same three
 * score columns, the same timer, the same review. A new id would have bought
 * nothing but a migration.
 */

export type ExamFamilyId = "telc" | "certyfikat";

/** Levels a certificate can be issued at, plus the one that is not a level. */
export type Level = "A1" | "A2" | "B1" | "B2" | "C1" | "C2" | "below";

/** The state exam runs a separate paper for adults and for 14-17-year-olds. */
export type CandidateGroup = "adults" | "young";

export type ModuleId = "listening" | "reading" | "elements" | "writing";

export interface ModuleSpec {
  id: ModuleId;
  /** Polish module name, exactly as printed on the paper. */
  name: string;
  /**
   * Time in minutes, or `null` when the exam publishes only a total for the
   * written paper and each sitting splits it its own way. A set with a `null`
   * here has to carry its own minutes.
   */
  minutes: number | null;
  /** Number of points, or `null` when it varies from paper to paper. */
  items: number | null;
}

/** Test modules that add up to a single partial result. */
export type TestModuleId = "listening" | "reading" | "elements";
export const TEST_MODULES = ["listening", "reading", "elements"] as const;

/** `min` is inclusive; the level is the first match from the top. */
export interface Threshold {
  level: Level;
  min: number;
  max: number;
}

/* ------------------------------------------------------------------ */
/* Pass rules                                                          */
/* ------------------------------------------------------------------ */

/**
 * How a paper turns into a certificate. The two exams disagree about this more
 * than about anything else, which is why it is data rather than a function.
 */
export type PassRule =
  /**
   * telc: the oral exam is a precondition, and one other module has to reach
   * the same level. One paper, two possible certificates. `scoring.ts` holds
   * the detail — it is too tangled to express as numbers.
   */
  | { kind: "telc" }
  /**
   * State exam, B1 and above: at least `ratio` of the points in *every* part,
   * the oral one included. Miss one part and the rest cannot make up for it.
   */
  | { kind: "perPart"; ratio: number }
  /**
   * State exam, A1/A2 and both children's levels: `ratio` of the written parts
   * taken together, and `ratio` of the oral. A weak part can be carried by a
   * strong one, as long as it is on the same side of the exam.
   */
  | { kind: "writtenAndOral"; ratio: number };

/* ------------------------------------------------------------------ */
/* Variants                                                            */
/* ------------------------------------------------------------------ */

/** `telc.b1b2.school`, `certyfikat.b1.adults` — the id a set points at. */
export type ExamVariantId = string;

export interface ExamVariant {
  id: ExamVariantId;
  family: ExamFamilyId;
  /**
   * The level(s) this paper can certify. telc lists two because one sitting
   * yields either; the state exam lists exactly one, because a candidate sits
   * the level they registered for and either clears it or does not.
   */
  levels: Level[];
  group: CandidateGroup;
  /** Written modules, in the order they are actually sat. */
  modules: ModuleSpec[];
  /** Total time for the written paper, in minutes. */
  writtenMinutes: number;
  /** Time for the oral exam, in minutes. */
  oralMinutes: number;
  pass: PassRule;
  /** Where the numbers above come from, for anyone checking them. */
  source: string;
}

export const moduleById = (variant: ExamVariant): Partial<Record<ModuleId, ModuleSpec>> =>
  Object.fromEntries(variant.modules.map((m) => [m.id, m]));

export const hasModule = (variant: ExamVariant, id: ModuleId): boolean =>
  variant.modules.some((m) => m.id === id);

/** The single level a state-exam paper certifies; `null` when a paper spans two. */
export const soleLevel = (variant: ExamVariant): Level | null =>
  variant.levels.length === 1 ? (variant.levels[0] ?? null) : null;

/* ------------------------------------------------------------------ */
/* Where a set's content came from                                     */
/* ------------------------------------------------------------------ */

/**
 * Who wrote the paper.
 *
 * `own` is ours, written for this app. `past-paper` is a real sitting the
 * examining body published afterwards: the paper, the recording, and a
 * transcript file that also carries the official answer key. A past paper has
 * to be labelled as one wherever it is offered — a candidate practising on the
 * real February 2024 B1 exam should know that is what they are sitting.
 */
export type SetSource = "own" | "past-paper";

export interface SetProvenance {
  source: SetSource;
  /** Who published it, e.g. "Państwowa Komisja ds. Poświadczania Znajomości Języka Polskiego jako Obcego". */
  publisher?: string;
  /** The sitting, as the publisher dates it: "4-5 lutego 2024". */
  sitting?: string;
  /** The page the paper was taken from. */
  url?: string;
  /**
   * A set of our own: the past paper whose shape it was written to, and the
   * topic of each task — kept so the next set is not written on the same ones.
   */
  template?: string;
  topics?: Record<string, string>;
}
